import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Field, ErrorBox } from "@/components/common";
import { Fingerprint, ShieldCheck } from "lucide-react";

const BRAND_IMG = "https://images.unsplash.com/photo-1538691929598-f3c649a7b92e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("credentials"); // credentials | mfa
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const finish = async () => { await refresh(); navigate("/today"); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) setStep("mfa"); else await finish();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verifyMfa = async (value) => {
    const c = value || code;
    if (c.length < 6) return;
    setBusy(true); setError(null);
    try { await api.post("/auth/mfa/verify", { code: c }); await finish(); }
    catch (err) { setError(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };

  const passkey = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/auth/passkeys/auth/options", { email: email || null });
      const credential = await startAuthentication({ optionsJSON: data.options });
      await api.post(`/auth/passkeys/auth/verify/${data.challenge_id}`, { credential });
      await finish();
    } catch (err) { setError(err?.name === "NotAllowedError" ? "Passkey prompt was cancelled." : errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-[hsl(222_47%_11%)]">
        <img src={BRAND_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-md bg-white text-[hsl(213_78%_42%)] grid place-items-center font-heading font-bold">V</div><span className="font-heading font-semibold">VoroSuite Sales</span></div>
          <div className="max-w-md">
            <h2 className="text-3xl font-heading font-semibold leading-tight">Who should I contact today, why this company, what should I say, and what happens next?</h2>
            <p className="mt-4 text-white/75 text-sm">Tell the system who you want to sell to. It finds suitable companies with evidence, assigns ownership, plans the next action and carries the opportunity into Core.</p>
          </div>
          <div className="meta text-white/60 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Passkeys, MFA and tenant isolation protect your organisation's data.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-[0_1px_2px_hsl(222_47%_11%/0.06)]" data-testid="login-card">
          <h1 className="text-xl font-heading font-semibold">{step === "mfa" ? "Verify it's you" : "Sign in"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{step === "mfa" ? "Enter the 6-digit code from your authenticator app, or a backup code." : "Use your work email and password, or a passkey."}</p>
          {error && <div className="mt-4"><ErrorBox error={error} /></div>}
          {step === "credentials" ? (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <Field label="Email"><Input type="email" autoComplete="username webauthn" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></Field>
              <Field label="Password"><Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></Field>
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</Button>
              <div className="relative text-center meta"><span className="bg-card px-2 relative z-10">or</span><div className="absolute inset-x-0 top-1/2 border-t border-border" /></div>
              <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={passkey} data-testid="login-passkey"><Fingerprint className="h-4 w-4 mr-2" /> Sign in with a passkey</Button>
            </form>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verifyMfa(v); }} data-testid="mfa-otp">
                  <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                </InputOTP>
              </div>
              <Field label="Or paste a backup code"><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="xxxxxxxx-xxxxxxxx" data-testid="mfa-backup-input" /></Field>
              <Button className="w-full" disabled={busy || code.length < 6} onClick={() => verifyMfa()} data-testid="mfa-totp-submit-button">{busy ? "Verifying…" : "Verify"}</Button>
              <button className="meta underline w-full" onClick={() => { setStep("credentials"); setCode(""); }}>Back to sign in</button>
            </div>
          )}
          <p className="meta mt-6">Invited to an organisation? Open the invite link you received. <Link to="/accept-invite" className="underline">I have an invite token</Link></p>
        </div>
      </div>
    </div>
  );
}
